import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authService, { TestNumber } from '../services/authService';
import Layout from '../components/Layout';
import AgentNumberManager, { useAgentNumbers } from '../components/agents/AgentNumberManager';
import { WhatsAppIcon } from '../components/icons/WhatsAppIcon';
import whatsappService, { WhatsAppMode } from '../services/whatsappService';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Eye,
  EyeOff,
  Copy,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Phone,
  Mail,
  Smartphone,
  ShieldCheck,
  User,
  Plus,
  Trash2,
  FlaskConical,
  HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import SalonTransferNumbersSection from "../components/SalonTransferNumbersSection";
import ServiceGroupsSection from "../components/leistungen/ServiceGroupsSection";

// Die Salon-Telefonnummer ist zugleich das Wählziel der Weiterleitung. Twilio
// wählt nur +E.164 — am 15.09.2026 scheiterten 17 Weiterleitungen an
// „069 42094909" aus genau diesem Feld. Das Backend normalisiert nationale
// Schreibweisen anhand des Profil-Landes; hier nur der Hinweis, damit die
// Ländervorwahl gar nicht erst fehlt.
const PHONE_DIALABLE = /^\+[1-9]\d{6,14}$/;
const phoneHint = (raw: string): string | null => {
  const s = raw.replace(/[\s\-()/.]/g, "");
  if (!s) return null;
  if (PHONE_DIALABLE.test(s)) return null;
  if (!s.startsWith("+")) {
    return "Bitte mit Ländervorwahl angeben (z. B. +49 69 1234567) — sonst kann der Agent den Anrufer nicht durchstellen.";
  }
  return "Diese Nummer ist so nicht wählbar. Erwartet: + Ländervorwahl und Rufnummer ohne führende 0.";
};
const apiErrorDetail = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
};

interface BusinessDetails {
  id: string;
  business_name: string;
  email: string;
  phone_number: string;
  is_active: boolean;
  created_at: string;
  whatsapp_number: string;
  customer_id: string;
  timeglobe_auth_key?: string;
  /** Rufnummer des Telefonagenten (Twilio-Nummer aus twilio_numbers) */
  twilio_agent_number?: string | null;
}

type ProfileTab = 'daten' | 'nummern' | 'leistungen' | 'test';

const PROFILE_TABS: { id: ProfileTab; label: string }[] = [
  { id: 'daten', label: 'Unternehmensdaten' },
  { id: 'nummern', label: 'Rufnummern & Salon-Zuteilung' },
  { id: 'leistungen', label: 'Leistungen' },
  { id: 'test', label: 'Testnummern' },
];

// Mirrors backend MAX_TEST_NUMBERS_PER_BUSINESS; the server is the real gate.
const MAX_TEST_NUMBERS = 2;

const ProfilePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  // Tab in the URL so the help page can deep-link straight to the section it
  // explains, and a reload keeps the user where they were.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: ProfileTab =
    (PROFILE_TABS.find((t) => t.id === searchParams.get('tab'))?.id) || 'daten';
  const selectTab = (tab: ProfileTab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'daten') next.delete('tab');
    else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  
  const [fetchLoading, setFetchLoading] = useState(true);
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const [showAuthKey, setShowAuthKey] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaveSuccess, setProfileSaveSuccess] = useState(false);

  // Per-account test numbers (kept in sync with the backend cap).
  const [testNumbers, setTestNumbers] = useState<TestNumber[]>([]);
  const [tnLoading, setTnLoading] = useState(true);
  const [tnPhone, setTnPhone] = useState('');
  const [tnLabel, setTnLabel] = useState('');
  const [tnAdding, setTnAdding] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [tnVerifying, setTnVerifying] = useState(false);

  // Agent numbers ("Teilagenten") and their salon assignment — owned by the
  // shared manager so the phone and WhatsApp sections stay in sync.
  const agentNumbers = useAgentNumbers();
  const [waMode, setWaMode] = useState<WhatsAppMode>('central');
  const [waModeSaving, setWaModeSaving] = useState(false);

  // Server value wins until the owner actively flips the switch.
  useEffect(() => {
    setWaMode(agentNumbers.whatsappMode);
  }, [agentNumbers.whatsappMode]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const fetchAllData = async () => {
      try {
        setFetchLoading(true);
        const [businessData, businessInfo] = await Promise.all([
          authService.getBusinessProfile(),
          authService.getBusinessInfo(),
        ]);
        const { timeglobe_auth_key, ...rest } = businessData;
        const merged: BusinessDetails = {
          ...(rest as BusinessDetails),
          twilio_agent_number: (businessInfo as { twilio_agent_number?: string | null })?.twilio_agent_number ?? null,
        };
        setBusinessDetails(merged);
        setEditName(merged.business_name ?? '');
        setEditEmail(merged.email ?? '');
        setEditPhone(merged.phone_number ?? '');
      } catch (err) {
        console.error('Error fetching business details:', err);
        setError('Profil konnte nicht geladen werden');
      } finally {
        setFetchLoading(false);
      }

      // Test numbers load independently so one failure doesn't hide the other.
      try {
        const nums = await authService.getTestNumbers();
        setTestNumbers(nums);
      } catch (err) {
        console.error('Error fetching test numbers:', err);
      } finally {
        setTnLoading(false);
      }

    };

    fetchAllData();
  }, [isAuthenticated, navigate]);

  const maskAuthKey = (key: string) => {
    if (!key) return '••••••••••••••••••••••••••••••••';
    return '*'.repeat(key.length);
  };

  const handleVisibilityToggle = () => {
    if (showAuthKey) {
      setShowAuthKey(false);
    } else {
      setPasswordDialogOpen(true);
    }
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPassword('');
    setPasswordError(null);
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError('Bitte geben Sie Ihr Passwort ein.');
      return;
    }

    setIsValidating(true);
    setPasswordError(null);
    
    try {
      const isValid = await authService.validatePassword(password);
      if (isValid) {
        try {
          const authKeyData = await authService.getTimeglobeAuthKey();
          setBusinessDetails(prev => ({
            ...prev!,
            timeglobe_auth_key: authKeyData.timeglobe_auth_key
          }));
          setShowAuthKey(true);
          handlePasswordDialogClose();
        } catch (authKeyError) {
          console.error('Error fetching Auth Key:', authKeyError);
          setPasswordError('Fehler beim Abrufen des Schlüssels.');
        }
      } else {
        setPasswordError('Ungültiges Passwort.');
      }
    } catch (err) {
      console.error('Password validation error:', err);
      setPasswordError('Fehler bei der Prüfung.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyAuthKey = () => {
    if (businessDetails?.timeglobe_auth_key) {
      navigator.clipboard.writeText(businessDetails.timeglobe_auth_key);
      setCopyFeedback('Kopiert!');
      setTimeout(() => setCopyFeedback(null), 2000);
      toast.success("API-Schlüssel kopiert");
    }
  };

  const handleSaveProfile = async () => {
    if (!businessDetails) return;
    setSavingProfile(true);
    setProfileSaveSuccess(false);
    try {
      await authService.updateBusinessInfo({
        business_name: editName.trim() || undefined,
        email: editEmail.trim() || undefined,
        phone_number: editPhone.trim() || undefined,
      });
      setBusinessDetails(prev => prev ? {
        ...prev,
        business_name: editName.trim() || prev.business_name,
        email: editEmail.trim() || prev.email,
        phone_number: editPhone.trim() || prev.phone_number,
      } : null);
      setProfileSaveSuccess(true);
      toast.success('Unternehmensdaten gespeichert');
      setTimeout(() => setProfileSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
      toast.error(apiErrorDetail(err, 'Speichern fehlgeschlagen'));
    } finally {
      setSavingProfile(false);
    }
  };

  // --- Test numbers --------------------------------------------------------
  const errMessage = (err: unknown, fallback: string): string =>
    err instanceof Error && err.message ? err.message : fallback;

  const refreshTestNumbers = async () => {
    try {
      const nums = await authService.getTestNumbers();
      setTestNumbers(nums);
    } catch (err) {
      console.error('Error refreshing test numbers:', err);
    }
  };

  const handleAddTestNumber = async () => {
    const phone = tnPhone.trim();
    if (!phone) return;
    setTnAdding(true);
    try {
      const row = await authService.addTestNumber(phone, tnLabel.trim() || undefined);
      setVerifyingPhone(row.phone_raw || phone);
      setOtpCode('');
      await refreshTestNumbers();
      toast.success('SMS-Code gesendet');
    } catch (err) {
      toast.error(errMessage(err, 'Code konnte nicht gesendet werden'));
    } finally {
      setTnAdding(false);
    }
  };

  const handleVerifyTestNumber = async () => {
    if (!verifyingPhone) return;
    setTnVerifying(true);
    try {
      await authService.verifyTestNumber(verifyingPhone, otpCode);
      setVerifyingPhone(null);
      setOtpCode('');
      setTnPhone('');
      setTnLabel('');
      await refreshTestNumbers();
      toast.success('Testnummer verifiziert');
    } catch (err) {
      toast.error(errMessage(err, 'Verifizierung fehlgeschlagen'));
    } finally {
      setTnVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (!verifyingPhone) return;
    try {
      await authService.addTestNumber(verifyingPhone);
      setOtpCode('');
      toast.success('Neuer Code gesendet');
    } catch (err) {
      toast.error(errMessage(err, 'Code konnte nicht gesendet werden'));
    }
  };

  const handleDeleteTestNumber = async (id: number) => {
    const deleted = testNumbers.find((t) => t.id === id);
    try {
      await authService.deleteTestNumber(id);
      // Only cancel the in-progress OTP form if it belongs to the deleted number.
      if (deleted && verifyingPhone && deleted.phone_raw === verifyingPhone) {
        setVerifyingPhone(null);
        setOtpCode('');
      }
      await refreshTestNumbers();
      toast.success('Testnummer entfernt');
    } catch (err) {
      toast.error(errMessage(err, 'Löschen fehlgeschlagen'));
    }
  };

  const handleWhatsAppMode = async (mode: WhatsAppMode) => {
    if (mode === waMode) return;
    setWaModeSaving(true);
    const previous = waMode;
    setWaMode(mode);
    try {
      await whatsappService.setMode(mode);
    } catch (err) {
      setWaMode(previous);
      toast.error(errMessage(err, 'Einstellung konnte nicht gespeichert werden'));
    } finally {
      setWaModeSaving(false);
    }
  };

  if (fetchLoading) {
    return (
      <Layout>
        <div className="flex flex-col justify-center items-center min-h-[60vh] gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-primary/25 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full absolute top-0 left-0 animate-spin"></div>
          </div>
          <p className="text-muted-foreground text-sm animate-pulse">Profil wird geladen...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-5xl mx-auto py-8 space-y-8 animate-in fade-in duration-500">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Unternehmensprofil</h1>
            <p className="text-muted-foreground mt-1">Verwalten Sie Ihre Einstellungen und Geschäftsdaten.</p>
          </div>
          {businessDetails?.is_active && (
            <Badge className="bg-success/10 text-success-strong hover:bg-success/20 border-success/25 px-3 py-1 text-sm font-medium">
              <CheckCircle2 className="w-3 h-3 mr-2" />
              Konto aktiv
            </Badge>
          )}
        </div>
        
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex gap-1 border-b border-border overflow-x-auto">
          {PROFILE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectTab(tab.id)}
              className={
                activeTab === tab.id
                  ? 'px-4 py-2 text-sm font-medium border-b-2 -mb-px border-[#1967D2] text-[#1967D2] whitespace-nowrap'
                  : 'px-4 py-2 text-sm font-medium border-b-2 -mb-px border-transparent text-muted-foreground hover:text-foreground whitespace-nowrap'
              }
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Leistungen shows the group list next to its preview and needs the room. */}
        <div className={activeTab === 'leistungen' ? 'max-w-5xl' : 'max-w-3xl'}>
          {activeTab === 'daten' && (
            <Card className="shadow-sm border-border">
              <CardHeader>
                <div>
                  <CardTitle className="text-lg">Unternehmensdaten</CardTitle>
                  <CardDescription>Alle wichtigen Informationen auf einen Blick</CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Name</Label>
                    <div className="flex items-center gap-3">
                      <User className="h-4 w-4 text-muted-foreground shrink-0" />
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Unternehmensname"
                        className="bg-muted border-border"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Dieser Name wird von den Terminassistenten (Telefonagent und WhatsApp-Agent) genutzt.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">E-Mail</Label>
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                      <Input
                        type="email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        placeholder="E-Mail"
                        className="bg-muted border-border"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Salon-Telefonnummer</Label>
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                      <Input
                        type="tel"
                        inputMode="tel"
                        value={editPhone}
                        onChange={(e) => setEditPhone(e.target.value)}
                        placeholder="+49 69 1234567"
                        className="bg-muted border-border"
                      />
                    </div>
                    {phoneHint(editPhone) && (
                      <p className="text-xs text-amber-600">{phoneHint(editPhone)}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Standard-Weiterleitungsnummer: hier landet der Anrufer, wenn der Agent ihn an einen Mitarbeiter im Salon zurückgibt — für alle Salons ohne eigene Nummer. Bitte mit Ländervorwahl, z. B. +49 69 1234567.
                    </p>
                  </div>

                  <SalonTransferNumbersSection />

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Telefon-Agent (Rufnummer)</Label>
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-md border border-border">
                      <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-muted-foreground font-mono">
                        {businessDetails?.twilio_agent_number || '—'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Twilio-Nummer für den Telefonagenten (aus Einrichtung).
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">WhatsApp-Agent</Label>
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-md border border-border">
                       <Smartphone className="h-4 w-4 text-muted-foreground shrink-0" />
                       <span className="font-medium text-muted-foreground">{businessDetails?.whatsapp_number || '—'}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="bg-[#1967D2] hover:bg-[#1558b0]"
                  >
                    {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Unternehmensdaten speichern
                  </Button>
                  {profileSaveSuccess && (
                    <span className="text-sm text-success-strong flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4" /> Gespeichert
                    </span>
                  )}
                </div>

                <div className="pt-6 border-t border-border">
                  <div className="space-y-3">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold flex items-center gap-2">
                      <ShieldCheck className="h-3 w-3" />
                      TimeGlobe API-Schlüssel
                    </Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          value={showAuthKey ? businessDetails?.timeglobe_auth_key || '' : maskAuthKey(businessDetails?.timeglobe_auth_key || '')}
                          disabled
                          className="bg-muted pr-10 font-mono text-sm border-border"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-10 w-10 text-muted-foreground hover:text-foreground"
                          onClick={handleVisibilityToggle}
                        >
                          {showAuthKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                      
                      {showAuthKey && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline" size="icon" onClick={handleCopyAuthKey}>
                                {copyFeedback ? <CheckCircle2 className="h-4 w-4 text-success-strong" /> : <Copy className="h-4 w-4" />}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Kopieren</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Der API-Schlüssel kann hier nicht geändert werden. Bei Fragen wenden Sie sich an den Support.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'nummern' && (
            <div className="space-y-8">
              {/* Each card assigns the numbers of its channel, so a tenant
                  without a phone agent still assigns its WhatsApp numbers. A
                  line that answers both channels has one salon set; both cards
                  edit it through the same drafts (useAgentNumbers). */}
              <Card className="shadow-sm border-border">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-[#1967D2] mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <CardTitle className="text-lg">Telefon-Agenten</CardTitle>
                        {/* Straight into the guide for exactly this screen. */}
                        <Link
                          to="/help?tab=nummern"
                          className="shrink-0 text-muted-foreground hover:text-[#1967D2] transition-colors"
                          title="Wie funktioniert das? Zur Anleitung"
                          aria-label="Anleitung zu Rufnummern & Salon-Zuteilung öffnen"
                        >
                          <HelpCircle className="h-5 w-5" />
                        </Link>
                      </div>
                      <CardDescription>
                        Jede Rufnummer kann auf bestimmte Salons beschränkt werden — Anrufer dieser
                        Nummer erreichen dann einen Agenten, der nur für diese Standorte bucht und
                        berät. Ohne Auswahl gilt: alle Salons.
                        <span className="block mt-2">
                          Die Weiterleitung ergibt sich daraus von selbst: Fragt jemand nach einem
                          Salon, für den eine andere Nummer zuständig ist, verbindet der Agent
                          direkt dorthin — am Telefon wie in WhatsApp. Dafür ist nichts weiter
                          einzutragen.
                        </span>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <AgentNumberManager
                    channel="voice"
                    numbers={agentNumbers.numbers}
                    sites={agentNumbers.sites}
                    loading={agentNumbers.loading}
                    drafts={agentNumbers.drafts}
                    onDraftsChange={agentNumbers.setDrafts}
                    showBuy
                    onReload={agentNumbers.reload}
                  />
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <WhatsAppIcon className="h-5 w-5 text-[#1967D2] mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <CardTitle className="text-lg">WhatsApp-Agenten</CardTitle>
                      <CardDescription>
                        Entweder eine zentrale WhatsApp-Nummer für alle Salons oder eine eigene
                        Nummer je Standort. Welche Salons eine WhatsApp-Nummer betreut, legst du
                        direkt hier fest — auch ohne Telefonagenten. Ohne Auswahl gilt: alle
                        Salons.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="flex flex-wrap gap-2">
                    {(
                      [
                        { id: 'central' as const, label: 'Eine Nummer für alle Salons' },
                        { id: 'per_site' as const, label: 'Eigene Nummer je Standort' },
                      ]
                    ).map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => handleWhatsAppMode(opt.id)}
                        disabled={waModeSaving}
                        className={
                          waMode === opt.id
                            ? 'text-sm px-3 py-1.5 rounded-full border border-[#1967D2] bg-[#1967D2] text-white transition-colors'
                            : 'text-sm px-3 py-1.5 rounded-full border border-border bg-background text-muted-foreground hover:border-[#1967D2] hover:text-foreground transition-colors'
                        }
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <AgentNumberManager
                    channel="whatsapp"
                    numbers={agentNumbers.numbers}
                    sites={agentNumbers.sites}
                    loading={agentNumbers.loading}
                    drafts={agentNumbers.drafts}
                    onDraftsChange={agentNumbers.setDrafts}
                    showWhatsAppConnect={waMode === 'per_site'}
                    emptyHint="Noch keine WhatsApp-Nummer verbunden — das passiert in der Einrichtung."
                    onReload={agentNumbers.reload}
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'leistungen' && <ServiceGroupsSection />}

          {activeTab === 'test' && (
            <Card className="shadow-sm border-border">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <FlaskConical className="h-5 w-5 text-[#1967D2] mt-0.5 shrink-0" />
                  <div>
                    <CardTitle className="text-lg">Testnummern</CardTitle>
                    <CardDescription>
                      Verifizierte Nummern verursachen keine Kosten – ideal zum Testen von WhatsApp &amp; Telefonagent.
                      Max. {MAX_TEST_NUMBERS} pro Account, je 5 Gratis-Tests pro Woche (Anrufe + WhatsApp).
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {tnLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Testnummern werden geladen…
                  </div>
                ) : testNumbers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Noch keine Testnummer hinterlegt.</p>
                ) : (
                  <ul className="space-y-2">
                    {testNumbers.map((tn) => {
                      const used = tn.used_this_week ?? 0;
                      const limit = tn.weekly_limit ?? 5;
                      const atLimit = used >= limit;
                      return (
                      <li
                        key={tn.id}
                        className="flex items-center justify-between gap-3 p-3 bg-muted rounded-md border border-border"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <span className="font-medium text-muted-foreground font-mono">
                              {tn.phone_raw || tn.phone_normalized}
                            </span>
                            {tn.label && <span className="ml-2 text-xs text-muted-foreground">{tn.label}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {tn.verified && (
                            <Badge
                              variant="outline"
                              className={`font-mono ${atLimit ? 'text-warning-strong border-warning/25 bg-warning/10' : 'text-muted-foreground border-border bg-card'}`}
                              title="Gratis-Testinteraktionen (Anrufe + WhatsApp) diese Woche"
                            >
                              {used}/{limit} Tests/Woche
                            </Badge>
                          )}
                          {tn.verified ? (
                            <Badge className="bg-success/10 text-success-strong hover:bg-success/20 border-success/25">
                              <ShieldCheck className="w-3 h-3 mr-1" /> Verifiziert
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-warning-strong border-warning/25 bg-warning/10">
                              Nicht verifiziert
                            </Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive-strong"
                            onClick={() => handleDeleteTestNumber(tn.id)}
                            aria-label="Testnummer entfernen"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                      );
                    })}
                  </ul>
                )}

                {verifyingPhone ? (
                  <div className="space-y-3 p-4 rounded-md border border-primary/25 bg-primary/10">
                    <p className="text-sm text-muted-foreground">
                      SMS-Code an <span className="font-mono font-medium">{verifyingPhone}</span> gesendet.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-stelliger Code"
                        inputMode="numeric"
                        className="w-40 bg-background font-mono tracking-widest"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !tnVerifying && otpCode.length >= 4) handleVerifyTestNumber();
                        }}
                      />
                      <Button
                        onClick={handleVerifyTestNumber}
                        disabled={tnVerifying || otpCode.length < 4}
                        className="bg-[#1967D2] hover:bg-[#1558b0]"
                      >
                        {tnVerifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Bestätigen
                      </Button>
                      <Button variant="ghost" onClick={handleResendOtp} disabled={tnVerifying}>
                        Code erneut senden
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setVerifyingPhone(null);
                          setOtpCode('');
                        }}
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-end gap-3 pt-4 border-t border-border">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                        Neue Testnummer
                      </Label>
                      <Input
                        value={tnPhone}
                        onChange={(e) => setTnPhone(e.target.value)}
                        placeholder="+49 …"
                        className="w-56 bg-muted border-border"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                        Bezeichnung (optional)
                      </Label>
                      <Input
                        value={tnLabel}
                        onChange={(e) => setTnLabel(e.target.value)}
                        placeholder="z. B. Mein Handy"
                        className="w-48 bg-muted border-border"
                      />
                    </div>
                    <Button
                      onClick={handleAddTestNumber}
                      disabled={tnAdding || !tnPhone.trim() || testNumbers.length >= MAX_TEST_NUMBERS}
                      className="bg-[#1967D2] hover:bg-[#1558b0]"
                    >
                      {tnAdding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                      Code per SMS senden
                    </Button>
                  </div>
                )}

                {testNumbers.length >= MAX_TEST_NUMBERS && !verifyingPhone && (
                  <p className="text-xs text-warning-strong">
                    Maximale Anzahl an Testnummern erreicht. Entferne eine, um eine neue hinzuzufügen.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

            <div className="text-center space-y-2 py-6">
              <p className="text-xs text-muted-foreground">
                Kunde seit {businessDetails?.created_at ? new Date(businessDetails.created_at).getFullYear() : '...'}
              </p>
              <p className="text-xs text-muted-foreground">
                ID: <span className="font-mono">{businessDetails?.id?.split('-')[0]}...</span>
              </p>
            </div>
        </div>

        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Sicherheitsüberprüfung</DialogTitle>
              <DialogDescription>
                Bitte bestätigen Sie Ihre Identität mit Ihrem Passwort, um den API-Schlüssel anzuzeigen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="auth-password">Passwort</Label>
                <Input
                  id="auth-password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                     setPassword(e.target.value);
                     setPasswordError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isValidating) {
                      handlePasswordSubmit();
                    }
                  }}
                  placeholder="Ihr Passwort eingeben"
                />
                {passwordError && (
                  <p className="text-sm text-destructive-strong flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {passwordError}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button variant="outline" onClick={handlePasswordDialogClose} disabled={isValidating}>
                Abbrechen
              </Button>
              <Button onClick={handlePasswordSubmit} disabled={isValidating}>
                {isValidating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Bestätigen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ProfilePage;
